// The business cashflow view (PRD CF4, rev. 1.7; Chris, 2026-10-23). Pure rules, shared by the API
// and the browser.
//
// The API sends dated flows; the browser buckets and filters them, so a filter needs no round trip
// (as Profitability does). Money in: each project's invoices on their expected receipt dates (CF3),
// booked in full and the pipeline weighted by probability, shown separately; open invoices entered
// by hand until QuickBooks brings AR (CF5); client expense reports issued and not yet paid. Money
// out: salaried payroll on the last day of each month, hourly payroll every other Friday (Employee
// Handbook), shared costs, project costs, and approved expense reimbursements. The forecast starts
// from the latest bank balance an admin entered, and shows the net and the running balance.

import { addDays, daysBetween, isIsoDate, lastDayOf, monthOf, type Iso, type Month } from "./dates";
import type { RevenueType } from "./revenue";

/** Partner funding and referral fees are paid by the partner, not the client, on their own terms. */
export const PARTNER_TYPES: readonly RevenueType[] = ["partner-funding", "referral"];

/** A bank balance as of a day, entered by an admin each month until QuickBooks provides it. */
export interface CashBalance {
  id: string;
  asOf: Iso;
  amount: number;
  note?: string;
  by: string;
  at: string;
}

/** An invoice already sent and not yet paid, entered by hand until QuickBooks AR replaces it (CF5). */
export interface OpenInvoice {
  id: string;
  clientName: string;
  number: string;
  amount: number;
  invoiceDate: Iso;
  /** Overrides the client's terms for this invoice, such as a partner's. */
  termsDays?: number;
  projectCode?: string;
  note?: string;
  /** Set when it is paid: it leaves the forecast. */
  paidOn?: Iso;
}

export interface CashSettings {
  balances: CashBalance[];
  openInvoices: OpenInvoice[];
  /** Net days for revenue the partner pays: partner funding and referrals. */
  partnerTerms: Partial<Record<RevenueType, number>>;
  /** A known hourly payday; paydays fall every 14 days either side. */
  hourlyPayAnchor: Iso;
}

export const defaultCashSettings = (): CashSettings => ({
  balances: [],
  openInvoices: [],
  partnerTerms: { "partner-funding": 45, referral: 45 },
  // Chris, 2026-10-23: Friday 2026-10-23 is an hourly payday.
  hourlyPayAnchor: "2026-10-23",
});

/** The balance the forecast starts from: the latest entered. */
export const latestBalance = (balances: readonly CashBalance[]): CashBalance | undefined => [...balances].sort((a, b) => b.asOf.localeCompare(a.asOf) || b.at.localeCompare(a.at))[0];

/** Every hourly payday from `from` to `to`, counted in 14-day steps from the anchor. */
export function hourlyPaydays(anchor: Iso, from: Iso, to: Iso): Iso[] {
  const offset = ((daysBetween(anchor, from) % 14) + 14) % 14;
  const out: Iso[] = [];
  for (let d = offset === 0 ? from : addDays(from, 14 - offset); d <= to; d = addDays(d, 14)) out.push(d);
  return out;
}

export type CashKind = "invoice" | "open-invoice" | "expense-rebill" | "salaried" | "hourly" | "unfilled" | "shared" | "project-costs" | "reimbursements";

export const CASH_KIND_LABEL: Readonly<Record<CashKind, string>> = {
  invoice: "Project invoices",
  "open-invoice": "Open invoices",
  "expense-rebill": "Client expense reports",
  salaried: "Salaried payroll",
  hourly: "Hourly payroll",
  unfilled: "Unfilled roles",
  shared: "Shared costs",
  "project-costs": "Project costs",
  reimbursements: "Expense reimbursements",
};

export const CASH_IN: readonly CashKind[] = ["invoice", "open-invoice", "expense-rebill"];
export const CASH_OUT: readonly CashKind[] = ["salaried", "hourly", "unfilled", "project-costs", "shared", "reimbursements"];

/** One dated movement of cash. Out is a positive amount with direction "out". */
export interface CashFlow {
  date: Iso;
  kind: CashKind;
  direction: "in" | "out";
  amount: number;
  /** 1 for booked work and company costs; the project's probability for the pipeline. */
  weight: number;
  pipeline: boolean;
  label: string;
  clientName?: string;
  projectId?: string;
  code?: string;
  /** Expected before the forecast starts and not yet paid: shown on the first day, flagged. */
  overdue?: boolean;
}

/** Moves a receipt expected before the start to the start, flagged overdue. */
export function dueFrom(start: Iso, expected: Iso): { date: Iso; overdue: boolean } {
  return expected < start ? { date: start, overdue: true } : { date: expected, overdue: false };
}

/**
 * Spreads a month's amount over its paydays: the last day for salaried pay, the hourly Fridays for
 * hourly pay. A month cut by the forecast's start keeps only the paydays from the start.
 */
export function payrollFlows(month: Month, salaried: number, hourly: number, paydays: readonly Iso[], start: Iso, weight = 1, pipeline = false): CashFlow[] {
  const out: CashFlow[] = [];
  const last = lastDayOf(month);
  if (salaried && last >= start) out.push({ date: last, kind: "salaried", direction: "out", amount: salaried, weight, pipeline, label: "Salaried payroll" });
  const days = paydays.filter((d) => monthOf(d) === month);
  const inWindow = days.filter((d) => d >= start);
  if (hourly && inWindow.length) for (const d of inWindow) out.push({ date: d, kind: "hourly", direction: "out", amount: hourly / days.length, weight, pipeline, label: "Hourly payroll" });
  return out;
}

export type CashZoom = "week" | "month";

export interface CashPeriod {
  key: string;
  from: Iso;
  to: Iso;
  /** Booked work and company costs, by kind; in positive, out positive. */
  booked: Partial<Record<CashKind, number>>;
  /** The pipeline, weighted by probability. */
  pipeline: Partial<Record<CashKind, number>>;
  bookedIn: number;
  bookedOut: number;
  pipelineIn: number;
  pipelineOut: number;
  /** Booked net and the running balance from the opening balance. */
  net: number;
  balance: number;
  /** With the weighted pipeline. */
  netWithPipeline: number;
  balanceWithPipeline: number;
  overdue: number;
}

export interface CashFilter {
  clientName?: string;
  pipeline: boolean;
}

const mondayFor = (d: Iso): Iso => {
  const dow = new Date(`${d}T00:00:00Z`).getUTCDay();
  return addDays(d, -((dow + 6) % 7));
};

/** Buckets flows into weeks (Monday to Sunday) or months from `start` to `end`. */
export function cashPeriods(flows: readonly CashFlow[], opening: number, start: Iso, end: Iso, zoom: CashZoom, filter: CashFilter): CashPeriod[] {
  const periods: CashPeriod[] = [];
  const keyOf = (d: Iso) => (zoom === "month" ? monthOf(d) : mondayFor(d));
  const byKey = new Map<string, CashPeriod>();
  for (let d = start; d <= end; ) {
    const key = keyOf(d);
    const to = zoom === "month" ? lastDayOf(key) : addDays(key, 6);
    const p: CashPeriod = { key, from: d, to: to > end ? end : to, booked: {}, pipeline: {}, bookedIn: 0, bookedOut: 0, pipelineIn: 0, pipelineOut: 0, net: 0, balance: 0, netWithPipeline: 0, balanceWithPipeline: 0, overdue: 0 };
    periods.push(p);
    byKey.set(key, p);
    d = addDays(to, 1);
  }
  for (const f of flows) {
    if (f.date < start || f.date > end) continue;
    if (f.pipeline && !filter.pipeline) continue;
    // A client filter narrows what's tied to a client; company costs stay, since they're paid regardless.
    if (filter.clientName && f.clientName !== undefined && f.clientName !== filter.clientName) continue;
    if (filter.clientName && f.clientName === undefined && f.direction === "in") continue;
    const p = byKey.get(keyOf(f.date));
    if (!p) continue;
    const amount = f.amount * f.weight;
    const bucket = f.pipeline ? p.pipeline : p.booked;
    bucket[f.kind] = (bucket[f.kind] ?? 0) + amount;
    if (f.pipeline) f.direction === "in" ? (p.pipelineIn += amount) : (p.pipelineOut += amount);
    else f.direction === "in" ? (p.bookedIn += amount) : (p.bookedOut += amount);
    if (f.overdue) p.overdue += amount;
  }
  let balance = opening;
  let withPipeline = opening;
  for (const p of periods) {
    p.net = p.bookedIn - p.bookedOut;
    p.netWithPipeline = p.net + p.pipelineIn - p.pipelineOut;
    balance += p.net;
    withPipeline += p.netWithPipeline;
    p.balance = balance;
    p.balanceWithPipeline = withPipeline;
  }
  return periods;
}

export function validateCashSettings(s: Partial<CashSettings>): string[] {
  const errors: string[] = [];
  const day = (v: unknown) => isIsoDate(v);
  if (!Array.isArray(s.balances) || !Array.isArray(s.openInvoices)) return ["The cash settings are incomplete."];
  for (const b of s.balances) {
    if (!day(b?.asOf)) errors.push("Each balance needs the date it was taken.");
    if (typeof b?.amount !== "number" || !Number.isFinite(b.amount) || Math.abs(b.amount) > 1e9) errors.push("Each balance needs an amount.");
  }
  for (const v of s.openInvoices) {
    if (typeof v?.clientName !== "string" || !v.clientName.trim()) errors.push("Each open invoice needs its client.");
    if (typeof v?.number !== "string" || !v.number.trim() || v.number.length > 40) errors.push("Each open invoice needs its number.");
    if (typeof v?.amount !== "number" || !(v.amount > 0) || v.amount > 1e8) errors.push("Each open invoice needs an amount above zero.");
    if (!day(v?.invoiceDate)) errors.push("Each open invoice needs its invoice date.");
    if (v?.termsDays !== undefined && (!Number.isInteger(v.termsDays) || v.termsDays < 0 || v.termsDays > 180)) errors.push("Payment terms are 0 to 180 days.");
    if (v?.paidOn !== undefined && !day(v.paidOn)) errors.push("A paid date isn't valid.");
  }
  for (const t of PARTNER_TYPES) {
    const n = s.partnerTerms?.[t];
    if (n !== undefined && (!Number.isInteger(n) || n < 0 || n > 180)) errors.push("Payment terms are 0 to 180 days.");
  }
  if (!day(s.hourlyPayAnchor) || new Date(`${s.hourlyPayAnchor}T00:00:00Z`).getUTCDay() !== 5) errors.push("The hourly payday to count from must be a Friday.");
  return [...new Set(errors)];
}
